import * as preact from "preact";
import * as _ from "underscore";

import * as deviceDetect from "deviceDetect";
import * as notificationUtils from "notificationUtils";
import * as pushNotifications from "pushNotifications";
import * as languageMap from "shared/languageMap";
import stores from "stores/stores";

interface State {
  thisDevice?:
    | {
        type: "haveSubscription";
        subscription: any;
        device: string;
      }
    | {
        type: "noSubscription";
      }
    | {
        type: "notSupported";
      };
  allSubscriptions?: (pushNotifications.ServerSubscription & {
    thisDevice?: boolean;
  })[];
  awaitingPushNotificationChange?: boolean;
}

class NotificationSettingsView extends preact.Component<{}, State> {
  constructor(props: {}) {
    super(props);

    this.state = {};

    this.getSubscriptionStatus();
  }

  async getSubscriptionStatus() {
    const subscriptionStatus = await pushNotifications.getSubscriptionStatus();
    await this.setState({
      thisDevice: subscriptionStatus,
    });

    const allSubscriptions =
      await pushNotifications.getServerSubscriptions(subscriptionStatus);

    this.setState({
      allSubscriptions,
    });
  }

  pushNotificationChanged = async (event, id: string) => {
    const subscription = this.state.allSubscriptions?.find(
      (subscription) => `pushNotificationsCheckbox-${subscription._id}` === id
    );

    if (event.currentTarget.checked) {
      await pushNotifications.reactivateSubscription(subscription);
    } else {
      if (
        confirm(
          `Are you sure you want to remove push notifications for: ${subscription.device}`
        )
      ) {
        this.setState({ awaitingPushNotificationChange: true });
        // Remove push notification
        await pushNotifications.removeSubscription(subscription._id);
        await this.getSubscriptionStatus();
        this.setState({ awaitingPushNotificationChange: false });

        // If there are no more push subscriptions, remove it from user.emailNotifications
        if (this.state.allSubscriptions?.length === 0) {
          notificationUtils.removeType("dailyPushReminder");
        }
      } else {
        // The user cancelled to reset the checkbox back to checked
        event.currentTarget.checked = true;
      }
    }
  };

  subscribeToPushNotifications = async () => {
    this.setState({ awaitingPushNotificationChange: true });
    if (!(await pushNotifications.subscribe(deviceDetect.deviceString()))) {
      alert(pushNotifications.deniedMessage());
      this.setState({ awaitingPushNotificationChange: false });
      return;
    }
    notificationUtils.addType("dailyPushReminder");
    await this.getSubscriptionStatus();
    this.setState({ awaitingPushNotificationChange: false });
  };

  render() {
    const enabled = notificationUtils.getEnabledNotifications();
    const languageString =
      languageMap.map[stores.user.currentLearningLanguage.value].english;

    const reminderEmailsEnabled = _.contains(enabled.types, "dailyReminder");

    return (
      <div>
        <p>
          Get reminded if you haven't met the daily goal of 500 words or 10
          flashcards.
        </p>
        <h3>Reminder languages</h3>
        <table class="settingsTable">
          {_.union(enabled.languages, [
            stores.user.currentLearningLanguage.value,
          ]).map((language) => (
            <tr>
              <td>
                <input
                  type="checkbox"
                  id={`reminderLanguageCheckbox-${language}`}
                  checked={_.contains(enabled.languages, language)}
                  onChange={(event) => {
                    if (event.currentTarget.checked) {
                      notificationUtils.addLanguage(language);
                    } else {
                      notificationUtils.removeLanguage(language);
                    }
                  }}
                ></input>
              </td>
              <td>
                <label for={`reminderLanguageCheckbox-${language}`}>
                  {languageMap.map[language].english}
                </label>
              </td>
            </tr>
          ))}
        </table>
        <h3 style={{ marginTop: 10 }}>Reminder methods</h3>
        <table class="settingsTable">
          <tr>
            <td>
              <input
                type="checkbox"
                class="reminderEmail"
                id="reminderEmailCheckbox"
                checked={reminderEmailsEnabled}
                onChange={() => {
                  if (reminderEmailsEnabled) {
                    notificationUtils.removeType("dailyReminder");
                  } else {
                    notificationUtils.addType("dailyReminder");
                  }
                }}
              ></input>
            </td>
            <td>
              <label for="reminderEmailCheckbox">Email</label>
            </td>
          </tr>

          {!this.state.awaitingPushNotificationChange &&
          _.contains(enabled.types, "dailyPushReminder")
            ? this.state.allSubscriptions?.map((subscription) => {
                const id = `pushNotificationsCheckbox-${subscription._id}`;

                return (
                  <tr>
                    <td>
                      <input
                        type="checkbox"
                        class="pushNotifications"
                        id={id}
                        checked={true}
                        onChange={(event) => {
                          this.pushNotificationChanged(event, id);
                        }}
                      ></input>
                    </td>
                    <td>
                      <label for={id}>
                        Notification for {subscription.device}{" "}
                        {subscription.thisDevice ? "(this device)" : null}
                      </label>
                    </td>
                  </tr>
                );
              })
            : null}
          {this.state.awaitingPushNotificationChange ||
          (_.contains(enabled.types, "dailyPushReminder") &&
            this.state.allSubscriptions?.some(
              (subscription) => subscription.thisDevice
            )) ? null : (
            <tr>
              <td>
                <input
                  type="checkbox"
                  class="pushNotifications"
                  id="pushNotificationsCheckbox"
                  checked={false}
                  onClick={() => this.subscribeToPushNotifications()}
                  disabled={this.state.thisDevice?.type === "notSupported"}
                ></input>
              </td>
              <td>
                <label for="pushNotificationsCheckbox">
                  Notifications
                  {this.state.thisDevice?.type === "notSupported" ? (
                    <small> - not supported in this browser</small>
                  ) : (
                    <small>
                      {" "}
                      - get notified on this device if you have a streak but
                      haven't yet reached your daily goal
                    </small>
                  )}
                </label>
              </td>
            </tr>
          )}
          {this.state.awaitingPushNotificationChange ? (
            <tr>
              <td colSpan={2}>
                <img src="/content/throbber.gif" />
              </td>
            </tr>
          ) : null}
        </table>
        {(window.navigator as any).standalone ||
        deviceDetect.isIOSApp() ? null : (
          <>
            <br />
            <table class="settingsTable">
              <tr>
                <td>
                  <strong>TIP!&nbsp;</strong>{" "}
                </td>
                <td>
                  <a
                    target="_blank"
                    href="https://forum.readlang.com/t/install-readlang-on-your-iphone-ipad-or-android-home-screen/45"
                  >
                    Install Readlang to your mobile home screen
                  </a>
                  , then open it and check the box above to get reminder
                  notifications on your mobile phone. This will help you keep up
                  your daily {languageString} reading habit!{" "}
                </td>
              </tr>
              {this.state.allSubscriptions?.length > 0 ? (
                <tr>
                  <td>
                    <strong>NOTE&nbsp;</strong>{" "}
                  </td>
                  <td>
                    Push notifications are new. If you come across any issues or
                    have any feedback, please let me know at{" "}
                    <a href="mailto:steve@readlang.com">steve@readlang.com</a>.
                  </td>
                </tr>
              ) : null}
            </table>
          </>
        )}
      </div>
    );
  }
}

export default NotificationSettingsView;
